import { SIGNUP_SSO_PARAM, SIGNUP_SSO_VALUE } from '@/services/signup-sso-routes';

/**
 * The URL params the legacy signup app accepted, read once when a visitor
 * enters the rebuilt flow. Landing pages, emails, printed mailers and QR codes
 * already link with these, so their meaning is kept: an unusable value reads
 * as absent rather than as an error.
 */
export type SignupLanguage = 'en' | 'es';

export interface LegacySignupParams {
  clientId?: string;
  subClientId?: string;
  language?: SignupLanguage;
  /** True only for `dtr=true` (case and surrounding spaces ignored). */
  directToReg: boolean;
  /** Present-but-empty (`?login`) is an empty string, absent is undefined. */
  login?: string;
  download?: string;
  /**
   * The legacy flow version a link asks for (`flow=health_plan`), trimmed and
   * lower-cased; absent when missing or empty.
   */
  flow?: string;
  /**
   * Set only by the employer SSO landing (`sso=1`), never by a legacy link:
   * the member signed in with their employer and follows the SSO path.
   */
  sso?: true;
}

// Client ids are numbers or short slugs today; anything else is a typo or an
// attempt to smuggle markup, and is dropped.
const CLIENT_REF_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

function clientRef(raw: string | null): string | undefined {
  if (raw === null) return undefined;
  const value = raw.trim();
  return CLIENT_REF_PATTERN.test(value) ? value : undefined;
}

// Marketing links use whatever code the author knows (es, es-MX, en-GB), so
// match on the base language. Anything we do not ship is ignored.
function language(raw: string | null): SignupLanguage | undefined {
  const value = raw?.trim().toLowerCase();
  if (!value) return undefined;
  if (value === 'es' || value.startsWith('es-')) return 'es';
  if (value === 'en' || value.startsWith('en-')) return 'en';
  return undefined;
}

function presence(raw: string | null): string | undefined {
  return raw === null ? undefined : raw.trim();
}

/** `URLSearchParams.get` returns the first value, so a repeated param keeps its first. */
export function parseLegacySignupParams(search: string): LegacySignupParams {
  const params = new URLSearchParams(search);
  const result: LegacySignupParams = {
    directToReg: params.get('dtr')?.trim().toLowerCase() === 'true',
  };
  const clientId = clientRef(params.get('client_id'));
  if (clientId !== undefined) result.clientId = clientId;
  const subClientId = clientRef(params.get('sub_client_id'));
  if (subClientId !== undefined) result.subClientId = subClientId;
  const lng = language(params.get('lng'));
  if (lng !== undefined) result.language = lng;
  const login = presence(params.get('login'));
  if (login !== undefined) result.login = login;
  const download = presence(params.get('download'));
  if (download !== undefined) result.download = download;
  const flow = params.get('flow')?.trim().toLowerCase();
  if (flow) result.flow = flow;
  if (params.get(SIGNUP_SSO_PARAM)?.trim() === SIGNUP_SSO_VALUE) result.sso = true;
  return result;
}
